import * as React from "react";
import { Link } from "react-router-dom";
import ActiveStatusControl from "../../core/ActiveStatusControl/ActiveStatusControl";
import BackButton from "../../core/BackButton/BackButton";
import ErrorModal from "../../core/ErrorModal/ErrorModal";
import Spinner from "../../core/Spinner/Spinner";
import Toast from "../../core/Toast/Toast";
import { ITranslationProps, translateComponent } from "../../helpers/translation";
import IClientEditMaster from "../../types/IClientEditMaster";
import IResponse from "../../types/IResponse";

interface IClientEditMasterEditProps extends ITranslationProps {
    loading: boolean;
    client: IClientEditMaster;
    canLoginAsClient: boolean;
    saveClient: (client: IClientEditMaster) => Promise<IResponse<IClientEditMaster> | null>;
    toggleClientStatus: (active: boolean) => void;
}

interface IClientEditMasterEditState {
    validated: boolean;
    client: IClientEditMaster;
    errorMessages: string[];
    showErrorModal: boolean;
}

class ClientEdit extends React.Component<IClientEditMasterEditProps, IClientEditMasterEditState> {

    public static getDerivedStateFromProps(nextProps: IClientEditMasterEditProps, prevState: IClientEditMasterEditState) {
        prevState.client = nextProps.client;
        return prevState;
    }

    private formElement: HTMLFormElement | null;
    private toastElement: Toast | null;

    constructor(props: IClientEditMasterEditProps) {
        super(props);

        this.state = {
            validated: false,
            client: {
                clientName: "",
                resourceTag: "",
                id: 0,
                isActive: true,
                adminLicenseKey: "",
                licenseKeys: [],
                description: ""
            },
            errorMessages: [],
            showErrorModal: false
        };

        this.onClientNameChange = this.onClientNameChange.bind(this);
        this.onDescriptionChange = this.onDescriptionChange.bind(this);
        this.onResourceTagChange = this.onResourceTagChange.bind(this);
        this.onSaveClick = this.onSaveClick.bind(this);
        this.toggleIsActive = this.toggleIsActive.bind(this);
        this.closeErrorModal = this.closeErrorModal.bind(this);
    }

    public render() {
        return (
            <div>
                {this.props.loading && <Spinner />}
                <BackButton />
                <h2>{this.props.client.id > 0 ? this.props.t("ClientEdit.EditClient") : this.props.t("ClientEdit.CreateClient")}</h2>

                <form ref={ref => this.formElement = ref} className={this.state.validated ? "was-validated" : "needs-validation"} noValidate={true}>

                    <div className="card mt-3">
                        <div className="card-header">
                            <h5>{this.props.t("ClientEdit.ClientInformation")}</h5>
                        </div>
                        <div className="card-body">
                            <div className="form-group">
                                <label>{this.props.t("ClientEdit.ClientName")}</label>
                                <input type="text" className="form-control" placeholder={this.props.t("ClientEdit.ClientName")}
                                    value={this.state.client.clientName} onChange={this.onClientNameChange} required={true} maxLength={150} autoFocus={true} />
                                <div className="invalid-feedback">
                                    {this.props.t("ClientEdit.ClientNameInvalid")}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{this.props.t("ClientEdit.DescriptionContactInformation")}</label>
                                <input type="text" className="form-control" placeholder={this.props.t("ClientEdit.Description")}
                                    value={this.state.client.description} onChange={this.onDescriptionChange} required={false} maxLength={500} />
                                <div className="invalid-feedback">
                                    {this.props.t("ClientEdit.DescriptionInvalid")}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{this.props.t("ClientEdit.ResourceTag")}</label>
                                <input type="text" className="form-control" placeholder={this.props.t("ClientEdit.ResourceTag")}
                                    value={this.state.client.resourceTag} onChange={this.onResourceTagChange} required={false} maxLength={50} />
                                <div className="invalid-feedback">
                                    {this.props.t("ClientEdit.ResourceTagInvalid")}
                                </div>
                            </div>

                            {this.props.client.id > 0 &&
                                <div className="row">
                                    <div className="col">
                                        <div className="form-group">
                                            <label>{this.props.t("ClientEdit.AdminLicenseKey")}</label>
                                            <div className="form-control">{this.state.client.adminLicenseKey}</div>
                                        </div>
                                    </div>
                                    <div className="col">
                                        <ActiveStatusControl
                                            isActive={this.state.client.isActive}
                                            label={this.props.t("ClientEdit.Status")}
                                            activateConfirmationMessage={this.props.t("ClientEdit.ConfirmActivate")}
                                            inactivateConfirmationMessage={this.props.t("ClientEdit.ConfirmInactivate")}
                                            onConfirmation={this.toggleIsActive} />
                                    </div>
                                </div>
                            }
                            {this.props.client.id > 0 && this.props.canLoginAsClient &&
                                <Link to={`/loginToClient/${this.props.client.id}`} title={`Login to ${this.props.client.clientName}`}>{this.props.t("ClientEdit.LoginLink")}</Link>
                            }
                        </div>
                    </div>

                    <div className="card mt-3">
                        <div className="card-header">
                            <h5>{this.props.t("ClientEdit.LicenseKeys")}</h5>
                        </div>
                        <div className="card-body">
                            {this.renderLicenseKeys()}
                        </div>
                    </div>

                    <button type="button" data-id="save-button" className="btn btn-primary mt-3 px-4"
                        onClick={this.onSaveClick}>{this.props.t("Global.SaveButton")}</button>
                </form>

                <ErrorModal
                    title={this.props.t("Global.Error")}
                    errors={this.state.errorMessages}
                    isOpen={this.state.showErrorModal}
                    onRequestClose={this.closeErrorModal} />

                <Toast ref={ref => this.toastElement = ref} />

            </div>
        );
    }

    private renderLicenseKeys(): JSX.Element {

        const licenseKeyRows = this.state.client.licenseKeys.map((licenseKey, index) => {
            return (
                <tr key={index}>
                    <td>{licenseKey.keyName}</td>
                    <td>{licenseKey.licenseKey}</td>
                    <td>{licenseKey.postbackAuthHeader}</td>
                    <td>{licenseKey.isActive ? this.props.t("Global.Active") : this.props.t("Global.Inactive")}</td>
                </tr>
            )
        });

        return (
            <table className="table table-sm">
                <thead className="thead-light">
                    <tr>
                        <th>Key Name</th>
                        <th>License Key</th>
                        <th>PostBack AuthHeader</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {licenseKeyRows}
                </tbody>
            </table>
        );
    }

    private closeErrorModal() {
        this.setState({ showErrorModal: false, errorMessages: [] });
    }

    private async onSaveClick() {
        this.setState({ validated: true });

        if (this.formElement && this.formElement.checkValidity()) {

            const response = await this.props.saveClient(this.state.client);
            if (response) {
                if (response.success) {
                    if (this.toastElement) {
                        this.toastElement.success(this.props.t("Global.Success"), "");
                    }
                }
                else {
                    this.setState({ showErrorModal: true, errorMessages: response.errorMessages })
                }
            }

            this.setState({ validated: false });
        }
    }

    private onClientNameChange(event: React.ChangeEvent<HTMLInputElement>) {
        const client = this.state.client;
        client.clientName = event.target.value;
        this.setState({ client });
    }

    private onDescriptionChange(event: React.ChangeEvent<HTMLInputElement>) {
        const client = this.state.client;
        client.description = event.target.value;
        this.setState({ client });
    }

    private onResourceTagChange(event: React.ChangeEvent<HTMLInputElement>) {
        const client = this.state.client;
        client.resourceTag = event.target.value;
        this.setState({ client });
    }

    private async toggleIsActive() {
        await this.props.toggleClientStatus(!this.state.client.isActive);
        if (this.toastElement) {
            this.toastElement.success(this.props.t("Global.Success"), "");
        }

    }
}

export default translateComponent(ClientEdit);



// WEBPACK FOOTER //
// ./src/components/ClientEdit/ClientEdit.tsx